import { Button } from "@/components/ui/button";
import { getSocket } from "@/lib/utils";
import { Loader2Icon, SendIcon, TerminalIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const cleanAnsi = (str: string) =>
    str.replace(/[][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g, '');

type LogLine = { id: number; text: string };

export default function QuickConsoleCard() {
    const [command, setCommand] = useState('');
    const [logs, setLogs] = useState<LogLine[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const outputRef = useRef<HTMLDivElement>(null);
    const socketRef = useRef<ReturnType<typeof getSocket> | null>(null);
    const idRef = useRef(0);

    const pushLines = (data: string) => {
        const lines = cleanAnsi(data)
            .split(/\r?\n/)
            .filter(l => l.length > 0)
            .map(text => ({ id: idRef.current++, text }));
        if (!lines.length) return;
        setLogs(prev => [...prev, ...lines].slice(-150));
    };

    useEffect(() => {
        socketRef.current = getSocket(['liveconsole']);

        socketRef.current.on('connect', () => {
            setIsConnected(true);
            pushLines('[SC System] Connected to live console feed.');
        });

        socketRef.current.on('disconnect', () => {
            setIsConnected(false);
            pushLines('[SC System] Connection lost.');
        });

        socketRef.current.on('consoleData', (data: string) => {
            pushLines(data);
        });

        return () => {
            socketRef.current?.removeAllListeners();
            socketRef.current?.disconnect();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [logs]);

    const handleSend = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = command.trim();
        if (!trimmed || !socketRef.current || !isConnected) return;
        socketRef.current.emit('consoleCommand', trimmed);
        setCommand('');
    };

    return (
        <div className="col-span-3 sm:col-span-1 2xl:col-span-3 py-2 px-4 flex flex-col md:rounded-xl border bg-card shadow-sm h-[20rem] max-h-[20rem]">
            <div className="flex flex-row items-center justify-between pb-2 text-muted-foreground">
                <h3 className="tracking-tight text-sm font-medium flex items-center gap-1.5 line-clamp-1">
                    <TerminalIcon size={14} className="text-scCyan" />
                    Quick Console
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-scGreen' : 'bg-destructive'}`} />
                </h3>
                <span className="text-[10px] font-mono text-muted/60">LIVE FEED</span>
            </div>

            <div
                ref={outputRef}
                className="flex-1 bg-black/40 border border-white/[0.03] text-[#00ffcc] font-mono text-[10px] p-2.5 rounded-lg overflow-y-auto mb-2 scrollbar-thin select-text"
            >
                {logs.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-muted-foreground font-sans">
                        <Loader2Icon className="animate-spin size-4 mr-2" /> Connecting logs feed...
                    </div>
                ) : (
                    logs.map(log => (
                        <div key={log.id} className="whitespace-pre-wrap break-all leading-tight">
                            {log.text}
                        </div>
                    ))
                )}
            </div>

            <form onSubmit={handleSend} className="flex gap-2">
                <input
                    type="text"
                    value={command}
                    onChange={e => setCommand(e.target.value)}
                    disabled={!isConnected}
                    placeholder={isConnected ? "Enter console command..." : "Terminal offline"}
                    className="flex-1 min-w-0 bg-white/[0.02] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-muted focus:outline-none focus:border-scCyan/50 focus:ring-1 focus:ring-scCyan/20 font-mono"
                />
                <Button
                    type="submit"
                    size="sm"
                    disabled={!isConnected || !command.trim()}
                    className="bg-scCyan/10 hover:bg-scCyan/20 text-scCyan border border-scCyan/20"
                >
                    <SendIcon size={12} />
                </Button>
            </form>
        </div>
    );
}
